import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  // Define background-only operations (these won't trigger the main loading$)
  // These load silently behind the non-blocking "Loading background data" indicator
  private readonly backgroundOperationNames = new Set<string>([
    // Search page UI templates
    'configuratorTestAnalysisSearchUiTemplate',
    'configuratorTestCaseSearchUiTemplate',
    'configuratorTestSuiteSearchUiTemplate',
    'configuratorTestSpecificationSearchUiTemplate',
    'configuratorSpecificationCoverageSearchUiTemplate',

    // Item page - form UI templates
    'configuratorTestAnalysisUiTemplate',
    'configuratorTestCaseUiTemplate',
    'configuratorTestSuiteUiTemplate',

    // Item page - dropdown data sources
    'configuratorAllOrganizationalGroups',
    'configuratorProductProperties',
    'configuratorAllOffboardComponents',
    'configuratorAllEcus',
    'configuratorTestObjects',
    'configuratorSop',
    'configuratorAllTestLevels',
    'configuratorAllTestSuites',
    'configuratorFpcs',
    'configuratorUserFunctions',
    'configuratorUsers',

    // List data
    'configuratorTestCasesPagination',
    'configuratorAllTestAnalysis',
    'configuratorAllTestCases',

    // Inline search from linking modals //this was hiding spinner for search in item-list. Double spinners in linking modal now
    // 'configuratorSearchTestAnalysis',
    // 'configuratorSearchTestSuite',
    // 'configuratorSearchTestCase',

    'configuratorTryGetLockedItemUidAndTypeByUser'
  ]);

  // Operations whose button already shows its own inline spinner - skip tracking entirely,
  // so neither the global overlay nor the background pill fires for them.
  private readonly silentOperationNames = new Set<string>([
    // Requirement pickers - Add button
    'mc2GetFileContent', 'configuratorEeseGetRequirement',
    // Polarion Requirement/Specification - Add button (exclusive to Add; the chained
    // connected-items lookup is also used by row-level pencil edits, so it's silenced
    // per-call via the skipGlobalLoading Apollo context flag instead - see apollo-client.service.ts)
    'polarionRequirementByIdAndIrma', 'polarionSpecificationByIdAndIrma',
    // Evaluate buttons (coverage pages)
    'configuratorSoftwareVersionCoverageEvaluationData', 'configuratorEeseCoverageEvaluationData', 'configuratorSpecificationCoverageEvaluationData',
    // Check Out
    'configuratorTryLockItem',
  ]);

  // Counters for tracking
  private foregroundLoadingCount = 0;
  private backgroundLoadingCount = 0;

  // Public observables
  private foregroundLoadingSubject = new BehaviorSubject<boolean>(false);
  public readonly loading$ = this.foregroundLoadingSubject.asObservable();

  private backgroundLoadingSubject = new BehaviorSubject<boolean>(false);
  public readonly loadingInBackground$ = this.backgroundLoadingSubject.asObservable();

  show(operationName: string): void {
    if (this.silentOperationNames.has(operationName)) return;

    const isBackground = this.backgroundOperationNames.has(operationName);

    // Increment foreground only if not background operation
    if (!isBackground) {
      this.foregroundLoadingCount++;
      if (this.foregroundLoadingCount === 1) {
        this.foregroundLoadingSubject.next(true);
      }
    } else {
      // Increment global/background count
      this.backgroundLoadingCount++;
      if (this.backgroundLoadingCount === 1) {
        this.backgroundLoadingSubject.next(true);
      }
    }
  }

  hide(operationName: string): void {
    if (this.silentOperationNames.has(operationName)) return;

    const isBackground = this.backgroundOperationNames.has(operationName);

    // Decrement global/background count
    if (isBackground && this.backgroundLoadingCount > 0) {
      this.backgroundLoadingCount--;
    }
    if (this.backgroundLoadingCount === 0) {
      this.backgroundLoadingSubject.next(false);
    }

    // Decrement foreground count only if not background operation
    if (!isBackground && this.foregroundLoadingCount > 0) {
      this.foregroundLoadingCount--;
    }
    if (this.foregroundLoadingCount === 0) {
      this.foregroundLoadingSubject.next(false);
    }
  }
}
